<template>
  <div class="profile-form">

    <div v-if="loading" class="loading-indicator">{{ t('loading') }}</div>
    <div v-else-if="loadError" class="error-message">{{ loadError }}</div>

    <template v-else>

      <div v-if="data.emailAddress" class="profile-row">
        <span class="label">{{ t('email') }}</span>
        <span class="value">{{ data.emailAddress }}</span>
      </div>

      <div class="form-grid">

        <div class="form-field">
          <label>{{ t('firstName') }}</label>
          <input v-model="data.firstName" type="text" autocomplete="given-name" />
        </div>

        <div class="form-field">
          <label>{{ t('lastName') }}</label>
          <input v-model="data.lastName" type="text" autocomplete="family-name" />
        </div>

        <div class="form-field full">
          <label>{{ t('street') }}</label>
          <input v-model="data.street" type="text" autocomplete="street-address" />
        </div>

        <div class="form-field">
          <label>{{ t('zipCode') }}</label>
          <input v-model="data.zipCode" type="text" autocomplete="postal-code" />
        </div>

        <div class="form-field">
          <label>{{ t('city') }}</label>
          <input v-model="data.city" type="text" autocomplete="address-level2" />
        </div>

        <div class="form-field">
          <label>{{ t('country') }}</label>
          <input v-model="data.country" type="text" autocomplete="country-name" />
        </div>

        <div class="form-field">
          <label>{{ t('phoneNumber') }}</label>
          <input v-model="data.phoneNumber" type="tel" autocomplete="tel" />
        </div>

        <template v-if="showAdminFields">
          <div class="form-field">
            <label>{{ t('number') }}</label>
            <input v-model="data.number" type="text" />
          </div>
          <div class="form-field full">
            <label>{{ t('flags') }}</label>
            <input v-model="data.flags" type="text" />
          </div>
        </template>

      </div>

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      <div v-if="successMessage" class="success-message">{{ successMessage }}</div>

      <div class="form-actions">
        <button :disabled="!hasChanges || saving" @click="save">
          {{ saving ? t('loading') : t('save') }}
        </button>
      </div>

    </template>

  </div>
</template>

<script>
import { useI18n } from "../i18n.js"

const EDITABLE_FIELDS = ['firstName', 'lastName', 'street', 'zipCode', 'city', 'country', 'phoneNumber']
const ADMIN_FIELDS = ['flags', 'number']

function emptyData() {
  return {
    emailAddress: '',
    firstName: '',
    lastName: '',
    street: '',
    zipCode: '',
    city: '',
    country: '',
    phoneNumber: '',
    flags: '',
    number: ''
  }
}

export default {
  name: "ChatProfileForm",

  props: {
    service: { type: Object, required: true },
    connectionId: { type: [String, Number], required: true },
    showAdminFields: { type: Boolean, default: false }
  },

  emits: ['saved'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      data: emptyData(),
      original: emptyData(),
      loading: true,
      loadError: '',
      saving: false,
      errorMessage: '',
      successMessage: ''
    }
  },

  computed: {
    fieldsToSubmit() {
      return this.showAdminFields
          ? [...EDITABLE_FIELDS, ...ADMIN_FIELDS]
          : EDITABLE_FIELDS
    },

    hasChanges() {
      return this.fieldsToSubmit.some(f => (this.data[f] || '') !== (this.original[f] || ''))
    }
  },

  async mounted() {
    await this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.loadError = ''
      try {
        const res = await this.service.getConnectionUser(this.connectionId)
        const filled = { ...emptyData(), ...res }
        // Normalise null → '' so v-model never gets a non-string and hasChanges stays correct.
        for (const k of Object.keys(filled)) {
          if (filled[k] == null) filled[k] = ''
        }
        this.data = { ...filled }
        this.original = { ...filled }
      } catch (e) {
        this.loadError = e.message || 'Load failed'
      } finally {
        this.loading = false
      }
    },

    async save() {
      if (!this.hasChanges || this.saving) return
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const payload = {}
        for (const f of this.fieldsToSubmit) {
          payload[f] = this.data[f] || ''
        }
        await this.service.updateConnectionUser(this.connectionId, payload)
        this.original = { ...this.data }
        this.successMessage = this.t('saved')
        this.$emit('saved', { ...this.data })
      } catch (e) {
        this.errorMessage = e.message || this.t('saveFailed')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.profile-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-row {
  display: flex;
  gap: 10px;
  font-size: 14px;
  padding: 8px 12px;
  background: #f7f8fa;
  border-radius: 8px;
}

.profile-row .label {
  min-width: 100px;
  color: #5f6b7a;
  font-weight: 500;
}

.profile-row .value {
  color: #1a1a2e;
  word-break: break-all;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.form-field.full {
  grid-column: 1 / -1;
}

.form-field label {
  font-size: 12px;
  font-weight: 500;
  color: #5f6b7a;
}

.form-field input {
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  background: #ffffff;
}

.form-field input:focus {
  border-color: #4f46e5;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.form-actions button {
  padding: 9px 16px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
}

.form-actions button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.loading-indicator {
  text-align: center;
  padding: 12px;
  color: #9ca3af;
  font-size: 13px;
}

@media (max-width: 480px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
